// Estado del cotizador con el modelo del paso «Cotizar» del wizard: plazo, y enganche o pago fijo.
// El motor local responde al instante; la simulación de la tienda manda cuando llega para esos términos.
import { useMemo, useState } from 'react';
import type {
    StoreQuoterPlan,
    StoreQuoterSimulation,
} from '@/api/services/loan-offerings/quoter.contract';
import { quoteWeeklyFromDownPayment } from '@/shared/domain/quote';
import { useDebounce } from '@/shared/hooks/useDebounce';
import {
    applyLinkedChange,
    fixLinkedField,
    INITIAL_FIXED,
    linkedTermsContext,
    paymentRange,
    type FixedField,
    type LinkedQuoteChange,
} from '@/domains/sales/wizard/linkedQuoteTerms';
import { promoSavings, type PromoSavings } from '@/domains/sales/wizard/promoSavings';
import { useDownPaymentLimits, useRecordQuoteLimits } from './useDownPaymentLimits';
import { useStoreQuoterPlans, useStoreQuoterSimulations } from './api/useStoreQuoter';
import {
    defaultPlanId,
    planLabel,
    quoterErrorMessage,
    resolveDownPayment,
    sortPlans,
    type DownPaymentBounds,
} from './lib/quoterPlans';

/** Espera tras mover el enganche o teclear el monto antes de volver a cotizar. */
const REQUEST_DEBOUNCE_MS = 250;

/** El servidor redondea el enganche que sale del porcentaje: un centavo de diferencia es el mismo. */
const DOWN_TOLERANCE_CENTS = 1;

export type QuoteStatus =
    | { kind: 'no-plan'; hint?: string }
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'no-feasible'; message: string }
    | { kind: 'ready' };

export interface TermQuote {
    termWeeks: number;
    simulation: StoreQuoterSimulation | null;
}

/** Números en pesos de la cotización a la vista: los del servidor o, mientras llegan, los locales. */
export interface QuoteNumbers {
    price: number;
    down: number;
    financed: number;
    openingFee: number;
    interest: number;
    iva: number;
    tna: number;
    weekly: number;
    weeks: number;
    total: number;
    creditCost: number;
    /** Ya los confirmó la simulación de la tienda. */
    confirmed: boolean;
}

export interface LinkedQuote {
    fixed: FixedField;
    down: number;
    minDown: number;
    maxDown: number;
    /** Mínimo del plan cuando una promoción lo bajó; `null` si no. */
    promoLoweredMinDown: number | null;
    weeks: number;
    terms: readonly number[];
    pay: number | null;
    payTarget: number | null;
    payRange: { min: number; max: number } | null;
    notice: string | null;
    setDown: (pesos: number) => void;
    change: (change: LinkedQuoteChange) => void;
    fix: (field: FixedField) => void;
}

export interface Quote {
    plans: StoreQuoterPlan[];
    plan: StoreQuoterPlan | null;
    selectPlan: (planId: string) => void;
    /** Enganche, plazo y pago ligados; `null` mientras no hay plan ni cotas. */
    linked: LinkedQuote | null;
    numbers: QuoteNumbers | null;
    savings: PromoSavings | null;
    terms: TermQuote[];
    termWeeks: number | null;
    status: QuoteStatus;
    downPaymentCents: number | null;
}

interface KeyedCents {
    key: string;
    cents: number;
}

interface FixedState {
    key: string;
    fixed: FixedField;
    payTarget: number | null;
    notice: string | null;
}

/** La simulación solo vale para el plan, el plazo y el precio que la pidieron. */
function matching(
    data: StoreQuoterSimulation | undefined,
    plan: StoreQuoterPlan | null,
    termWeeks: number,
    priceCents: number,
): StoreQuoterSimulation | null {
    return data &&
        plan &&
        data.planId === plan.planId &&
        data.termWeeks === termWeeks &&
        data.productPriceCents === priceCents
        ? data
        : null;
}

const toCents = (pesos: number): number => Math.round(pesos * 100);

export function useQuote(storeId: string, priceCents: number, productSku?: string): Quote {
    const plansQuery = useStoreQuoterPlans(storeId);
    const plans = useMemo(() => sortPlans(plansQuery.data ?? []), [plansQuery.data]);

    const [chosenPlanId, setChosenPlanId] = useState<string | null>(null);
    const [chosenDownState, setChosenDownState] = useState<KeyedCents | null>(null);
    const [chosenTerm, setChosenTerm] = useState<number | null>(null);
    const [fixedState, setFixedState] = useState<FixedState | null>(null);

    const plan =
        plans.find((item) => item.planId === chosenPlanId) ??
        plans.find((item) => item.planId === defaultPlanId(plans)) ??
        null;
    const termOptions = plan?.termWeeksOptions ?? [];
    const termWeeks =
        chosenTerm !== null && termOptions.includes(chosenTerm)
            ? chosenTerm
            : (termOptions.at(0) ?? null);

    const { bounds, noFeasible, ...limits } = useDownPaymentLimits(plan, priceCents);
    // Otro plan u otro precio arrancan en el mínimo del plan nuevo, con el enganche fijo.
    const fixedKey = `${plan?.planId ?? ''}|${String(priceCents)}`;
    const chosenDownPayment = chosenDownState?.key === fixedKey ? chosenDownState.cents : null;
    const setChosenDownPayment = (cents: number): void => {
        setChosenDownState({ key: fixedKey, cents });
    };
    const downPaymentCents = bounds ? resolveDownPayment(chosenDownPayment, bounds) : null;
    const own = fixedState?.key === fixedKey ? fixedState : null;
    const fixed = own?.fixed ?? INITIAL_FIXED;
    const payTarget = own?.payTarget ?? null;

    // Solo se simula el estado asentado: plan, precio y enganche ya resueltos dentro de las cotas.
    const requestKey = `${plan?.planId ?? ''}|${String(priceCents)}|${String(downPaymentCents ?? '')}|${productSku ?? ''}`;
    const debouncedKey = useDebounce(requestKey, REQUEST_DEBOUNCE_MS);
    const requestPrice = Number(debouncedKey.split('|')[1]);
    const input =
        debouncedKey === requestKey &&
        downPaymentCents !== null &&
        priceCents > 0 &&
        noFeasible === null
            ? {
                  priceCents,
                  downPaymentPct: downPaymentCents / priceCents,
                  productSku,
              }
            : null;
    const results = useStoreQuoterSimulations(storeId, plan?.planId ?? null, termOptions, input);

    const terms: TermQuote[] = termOptions.map((term, index) => ({
        termWeeks: term,
        simulation: matching(results.at(index)?.data, plan, term, priceCents),
    }));
    const selectedIndex = termWeeks === null ? -1 : termOptions.indexOf(termWeeks);
    const selected = selectedIndex === -1 ? null : (results.at(selectedIndex) ?? null);
    // Mientras llega la nueva, la anterior es un marcador: puede ser de otro SKU, no confirma nada.
    const simulation =
        selected === null || selected.isPlaceholderData
            ? null
            : (terms[selectedIndex]?.simulation ?? null);

    useRecordQuoteLimits(
        { bounds, noFeasible, ...limits },
        { priceCents, requestPrice, quote: simulation, rejection: selected?.error ?? null },
    );

    // La TNA de la simulación del plan (aun la anterior) ya trae la promoción; sin ella, la del plan.
    const tna =
        terms[selectedIndex]?.simulation?.tnaAnnual ??
        simulation?.tnaAnnual ??
        plan?.tnaAnnual ??
        null;
    const price = priceCents / 100;
    const context =
        plan && bounds && tna !== null
            ? linkedTermsContext({
                  price,
                  minDown: bounds.minCents / 100,
                  maxDown: bounds.maxCents / 100,
                  terms: termOptions,
                  tna: () => tna,
                  openingFeePct: plan.openingCommissionPct,
                  vatRate: plan.ivaOnInterest,
              })
            : null;

    const numbers =
        plan && downPaymentCents !== null && termWeeks !== null && tna !== null
            ? quoteNumbers({
                  plan,
                  priceCents,
                  downPaymentCents,
                  termWeeks,
                  tna,
                  simulation,
              })
            : null;
    const savings =
        plan && numbers
            ? promoSavings({
                  price,
                  downPayment: numbers.down,
                  weeks: numbers.weeks,
                  baseTna: plan.tnaAnnual,
                  appliedTna: numbers.tna,
                  openingFeePct: plan.openingCommissionPct,
              })
            : null;

    const change = (next: LinkedQuoteChange): void => {
        if (context === null || downPaymentCents === null || termWeeks === null) return;
        const resolved = applyLinkedChange(
            { fixed, payTarget, down: downPaymentCents / 100, weeks: termWeeks },
            next,
            context,
        );
        setChosenDownPayment(toCents(resolved.down));
        setChosenTerm(resolved.weeks);
        setFixedState({
            key: fixedKey,
            fixed: resolved.fixed,
            payTarget: resolved.payTarget,
            notice: resolved.notice,
        });
    };

    const fix = (field: FixedField): void => {
        const next = fixLinkedField(field, numbers?.weekly ?? null);
        if (next === null) return;
        setFixedState({ key: fixedKey, ...next, notice: null });
    };

    const linked: LinkedQuote | null =
        context && bounds && plan && downPaymentCents !== null && termWeeks !== null
            ? {
                  fixed,
                  down: downPaymentCents / 100,
                  minDown: context.minDown,
                  maxDown: context.maxDown,
                  promoLoweredMinDown: promoLoweredMin(plan, bounds, price),
                  weeks: termWeeks,
                  terms: termOptions,
                  pay: numbers?.weekly ?? null,
                  payTarget: fixed === 'pay' ? payTarget : null,
                  payRange: paymentRange(termWeeks, context),
                  notice: own?.notice ?? null,
                  setDown: (pesos) => {
                      setChosenDownPayment(toCents(pesos));
                  },
                  change,
                  fix,
              }
            : null;

    return {
        plans,
        plan,
        selectPlan: setChosenPlanId,
        linked,
        numbers,
        savings,
        terms,
        termWeeks,
        status: noFeasible
            ? { kind: 'no-feasible', message: noFeasible }
            : quoteStatus(storeId, plansQuery, plan, selected?.error ?? null, numbers),
        downPaymentCents,
    };
}

/** El mínimo del plan cuando la promoción lo bajó; un piso mayor es política, no promoción. */
function promoLoweredMin(
    plan: StoreQuoterPlan,
    bounds: DownPaymentBounds,
    price: number,
): number | null {
    return bounds.minPct < plan.minDownPaymentPct ? price * plan.minDownPaymentPct : null;
}

/** Con la simulación de esos términos mandan sus montos; sin ella, el motor local del wizard. */
function quoteNumbers(input: {
    plan: StoreQuoterPlan;
    priceCents: number;
    downPaymentCents: number;
    termWeeks: number;
    tna: number;
    simulation: StoreQuoterSimulation | null;
}): QuoteNumbers | null {
    const { plan, priceCents, downPaymentCents, termWeeks, tna, simulation } = input;
    const price = priceCents / 100;
    if (
        simulation &&
        Math.abs(simulation.downPaymentCents - downPaymentCents) <= DOWN_TOLERANCE_CENTS
    ) {
        return {
            price,
            down: simulation.downPaymentCents / 100,
            financed: simulation.financedAmountCents / 100,
            openingFee: simulation.openingCommissionCents / 100,
            interest: simulation.totalInterestCents / 100,
            iva: simulation.totalIvaCents / 100,
            tna: simulation.tnaAnnual,
            weekly: simulation.weeklyPaymentCents / 100,
            weeks: simulation.termWeeks,
            total: simulation.totalCents / 100,
            creditCost: simulation.creditCostCents / 100,
            confirmed: true,
        };
    }
    const down = downPaymentCents / 100;
    const local = quoteWeeklyFromDownPayment({
        price,
        downPayment: down,
        weeks: termWeeks,
        tna,
        openingFeePct: plan.openingCommissionPct,
        vatRate: plan.ivaOnInterest,
    });
    if (!local.valid) return null;
    const total = down + local.totals.payment;
    return {
        price,
        down,
        financed: local.financed,
        openingFee: Math.round(toCents(local.financed) * plan.openingCommissionPct) / 100,
        interest: local.totals.interest,
        iva: local.totals.vat,
        tna,
        weekly: local.weeks[0]?.payment ?? 0,
        weeks: termWeeks,
        total,
        creditCost: total - price,
        confirmed: false,
    };
}

function quoteStatus(
    storeId: string,
    plansQuery: { isLoading: boolean; error: unknown },
    plan: StoreQuoterPlan | null,
    simulationError: unknown,
    numbers: QuoteNumbers | null,
): QuoteStatus {
    if (storeId === '') return { kind: 'no-plan' };
    if (plansQuery.isLoading) return { kind: 'loading' };
    if (plansQuery.error)
        return { kind: 'no-plan', hint: quoterErrorMessage(plansQuery.error, '') };
    if (!plan) return { kind: 'no-plan' };
    if (simulationError) {
        return { kind: 'error', message: quoterErrorMessage(simulationError, planLabel(plan)) };
    }
    return numbers ? { kind: 'ready' } : { kind: 'loading' };
}
